import type { ImageKey } from "@/data/images";

export type Service = {
  slug: string;
  title: string;
  short: string;
  intro: string;
  image: ImageKey;
  points: string[];
  body: { heading: string; text: string }[];
  seoTitle?: string;
  seoDescription?: string;
  faq?: { q: string; a: string }[];
};


export const services: Service[] = [
  {
    slug: "particuliere-verhuizing",
    title: "Particuliere verhuizing",
    short: "Van studio tot vrijstaande woning: wij nemen je complete huisverhuizing uit handen.",
    intro:
      "Je nieuwe huis is rond en dan begint het echte werk. Wij zorgen dat je op de verhuisdag alleen nog de sleutel hoeft om te draaien. Ons team demonteert, verpakt, tilt en zet alles op de juiste plek in je nieuwe woning.",
    image: "particulier",
    seoTitle: "Particuliere verhuizing | Verhuisbedrijf Vakkundig",
    seoDescription:
      "Particuliere verhuizing door heel Nederland: inpakken, demontage, montage en transport door ervaren verhuizers. 4,9/5 uit 507 reviews. Gratis offerte op maat.",
    points: [
      "Meubels demonteren en in je nieuwe woning weer opbouwen",
      "Kwetsbare spullen apart ingepakt en gemarkeerd",
      "Bakwagen met laadklep, ook bij smalle straten en trappenhuizen",
      "Verhuizen op zaterdag of zondag is geen probleem",
    ],
    body: [
      {
        heading: "Hoe wij een woningverhuizing aanpakken",
        text: "We beginnen met een gratis opname, live of per video. Daarna weet je precies wat er gebeurt, hoeveel verhuizers er komen en wat het kost. Op de verhuisdag staat het team op tijd voor de deur en werken we van kamer naar kamer, zodat je overzicht houdt.",
      },
      {
        heading: "Zelf doen of alles uitbesteden",
        text: "Je bepaalt zelf hoeveel je uit handen geeft. Wil je zelf inpakken? Dan leveren wij het complete verhuismateriaal, van stevige dozen tot wikkelfolie en dekens. Kies je voor volledige begeleiding, dan verzorgen onze verhuizers het inpakken, de demontage en montage en het transport van begin tot eind. Grote meubels worden standaard beschermd met wikkelfolie en verhuisdekens, zodat je inboedel in dezelfde staat aankomt als hij vertrok.",
      },
      {
        heading: "Spoedverhuizing: vandaag bellen, morgen verhuizen",
        text: "Moet je met spoed verhuizen? Bij een spoedverhuizing schuiven wij mensen en wagens vrij, meestal binnen 24 tot 48 uur. Dat gebeurt vaker dan je denkt: een huurcontract dat plots eindigt, een woning die eerder wordt opgeleverd of een spoedverhuizing voor een familielid. Bel 085 060 1023 en we zeggen direct wat er die dag of de volgende dag mogelijk is, inclusief verhuislift, inpakservice en tijdelijke opslag.",
      },
    ],
    faq: [
      {
        q: "Wat kost een verhuizing gemiddeld?",
        a: "Dat hangt af van je inboedel in m³, de verdieping en bereikbaarheid van beide adressen, de afstand en de extra's die je kiest. Vul het inboedelformulier in, dan krijg je een offerte op maat — vrijblijvend en zonder verrassingen achteraf.",
      },
      {
        q: "Kan ik met spoed verhuizen?",
        a: "Ja. Voor een spoedverhuizing houden we capaciteit vrij en kunnen we meestal binnen 24 tot 48 uur bij je staan, ook in het weekend. Bel 085 060 1023 of stuur een WhatsApp-bericht.",
      },
      {
        q: "Verhuizen jullie ook in het weekend?",
        a: "Ja, wij werken zeven dagen per week van 09:00 tot 18:00, dus ook op zaterdag en zondag en in de avonduren.",
      },
    ],
  },

  {
    slug: "zakelijke-verhuizing",
    title: "Zakelijke verhuizing",
    short: "Kantoorverhuizing of bedrijfsverhuizing zonder stilstand: vrijdag inpakken, maandag gewoon doorwerken.",
    intro:
      "Een succesvolle kantoor- of bedrijfsverhuizing vraagt om vlekkeloze projectlogistiek, zorgvuldige planning en maximale betrouwbaarheid. Vakkundig Verhuizingen & Transport zorgt voor een naadloze overgang zonder productiviteitsverlies voor uw medewerkers. Onze operationele kracht bewijzen we bij veeleisende opdrachtgevers: zo verzorgen wij voor de Gemeente Den Haag de complete logistieke coördinatie, opbouw en afbouw van alle stemlocaties tijdens de verkiezingen, en zijn we een vertrouwd gezicht voor de logistieke coördinatie bij World Forum The Hague. Beoordeeld met een 9,9 op Trustoo en gecertificeerd voor beroepsvervoer, bent u verzekerd van een strak georganiseerde bedrijfsverhuizing volgens afspraak.",
    image: "zakelijk",
    seoTitle: "Bedrijfsverhuizing Den Haag & Zuid-Holland | Zakelijke verhuizing | Vakkundig",
    seoDescription:
      "Bedrijfsverhuizing in Den Haag, Voorburg, Zoetermeer, Rijswijk, Delft en Rotterdam: kantoor, praktijk of winkel zonder productiviteitsverlies. Vaste prijs, Trustoo 9,9, offerte binnen 24 uur.",
    points: [
      "Gratis kennismaking op locatie of via video, offerte binnen 24 uur",
      "Eén vaste projectleider en een verhuisplan met vaste planning",
      "Werkplekken genummerd, zodat alles op de juiste plek terugkomt",
      "IT, dossiers en archief zorgvuldig en vertrouwelijk behandeld",
      "Gefaseerde projectplanning zonder productiviteitsverlies",
      "Eén vaste totaalprijs, zonder verrassingen achteraf",
    ],
    body: [
      {
        heading: "Zo verloopt jullie kantoorverhuizing in 4 stappen",
        text: "1. Kennismaking: we bekijken de huidige en nieuwe locatie, op afspraak of via een video. 2. Verhuisplan: plattegrond, nummersysteem, planning en één totaalprijs op papier. 3. Verhuisdag: ons team demonteert, verpakt, vervoert en bouwt alles weer op, volgens een vooraf afgestemde planning. 4. Oplevering: werkplekken staan klaar, de oude locatie is leeg en netjes, en wij nemen verpakkingsmateriaal weer mee.",
      },
      {
        heading: "Bedrijfsverhuizing in Den Haag en heel Zuid-Holland",
        text: "Wij verzorgen bedrijfsverhuizingen vanuit Den Haag door heel Zuid-Holland: van een kantoorverhuizing in Den Haag, Rijswijk of Leidschendam-Voorburg tot een bedrijfsverhuizing in Zoetermeer, Delft, Leiden of Rotterdam. Omdat wij de regio, de binnensteden, de vergunningen voor laden en lossen en de bedrijventerreinen kennen, plannen we elke zakelijke verhuizing realistisch en zonder verrassingen.",
      },
      {
        heading: "Voor wie verzorgen wij zakelijke verhuizingen?",
        text: "Kantoren en zakelijke dienstverleners, huisarts-, tandarts- en fysiotherapiepraktijken, winkels en showrooms, scholen en maatschappelijke organisaties, en overheidslocaties. Of het nu gaat om tien werkplekken of een complete afdeling over meerdere verdiepingen: dezelfde aanpak met vaste projectleider, vast verhuisplan en vaste prijs.",
      },
      {
        heading: "Doorwerken tijdens de verhuizing",
        text: "We inventariseren de inboedel, maken een plattegrond van de nieuwe locatie en werken met een nummersysteem. Zo staat elk bureau, elke kast en elke doos direct op de goede plek en hoeft niemand te zoeken. Eén aanspreekpunt houdt de planning in de hand en schakelt kort met jullie facilitair of kantoormanager.",
      },

      {
        heading: "Montage, demontage en eigen materiaal",
        text: "Bureaus, kasten, wanden en vergadertafels demonteren we en bouwen we op de nieuwe locatie weer op. We werken met eigen wagens, verhuisliften, rollers, dekens en wikkelfolie, zodat we niet afhankelijk zijn van derden en kwetsbaar meubilair beschermd de deur uit gaat.",
      },
      {
        heading: "Krappe locaties, garages en complexe panden",
        text: "Een binnenstad, een smalle binnenplaats of aanvoer via de parkeergarage: we bekijken vooraf de route, de liften en de laad- en losmogelijkheden. Waar nodig verhuizen we gefaseerd volgens een strak projectplan, zodat jullie dienstverlening geen dag stilstaat.",
      },
      {
        heading: "Evenementen, op- en afbouw en logistiek",
        text: "Naast kantoren en praktijken verzorgen wij ook logistiek op locatie: transport van meubilair en decor, opbouw voor een beurs of congres en afbouw direct na afloop. Strak gepland, met een team dat gewend is aan strikte tijdvensters en werken op publiekslocaties.",
      },
      {
        heading: "Ook opslag en ontruiming",
        text: "Blijft er meubilair over of past niet alles in de nieuwe ruimte? Wij pakken het in, registreren het en slaan het tijdelijk droog op. Wat weg kan, voeren we gescheiden en verantwoord af, zodat de oude locatie netjes en leeg wordt opgeleverd.",
      },
    ],
    faq: [
      {
        q: "Wat kost een kantoorverhuizing?",
        a: "Bij een kantoorverhuizing bepalen het aantal werkplekken, het meubilair, de verdiepingen en liften en het gewenste tijdvenster de prijs. Wij komen langs of doen een video-opname en sturen daarna één totaalprijs, inclusief demontage, montage en afvoer van wat weg kan.",
      },
      {
        q: "Hoe lang staat mijn bedrijf stil bij een bedrijfsverhuizing?",
        a: "Zo min mogelijk. Met een gefaseerd verhuisplan en genummerde werkplekken, zodat iedereen de volgende ochtend op de nieuwe locatie aan de slag kan.",
      },
      {
        q: "Verzorgen jullie ook IT, archief en dossiers?",
        a: "Ja. Computers, servers, dossiers en archief behandelen we vertrouwelijk en verpakt, en we zetten alles op de juiste plek terug volgens de plattegrond van de nieuwe locatie.",
      },
      {
        q: "Hoe snel kunnen jullie een bedrijfsverhuizing inplannen?",
        a: "Binnen 24 uur heb je een offerte. Een kleine kantoorverhuizing plannen we vaak al binnen één tot twee weken; bij grotere projecten maken we samen een planning in fases.",
      },
      {
        q: "Doen jullie bedrijfsverhuizingen in Voorburg, Zoetermeer en Rotterdam?",
        a: "Ja. Vanuit Den Haag verzorgen we bedrijfsverhuizingen in heel Zuid-Holland, waaronder Leidschendam-Voorburg, Zoetermeer, Rijswijk, Delft, Leiden en Rotterdam. Ook verhuizingen van of naar een andere regio plannen we in.",
      },
      {
        q: "Wat is het verschil tussen een kantoorverhuizing en een bedrijfsverhuizing?",
        a: "Een kantoorverhuizing gaat over werkplekken, IT en archief. Een bedrijfsverhuizing kan breder zijn: ook een winkel, praktijk, werkplaats of magazijn. Wij verzorgen beide met dezelfde aanpak: één projectleider, een verhuisplan en één totaalprijs.",
      },
      {
        q: "Verhuizen jullie ook praktijken, winkels en overheidslocaties?",
        a: "Ja. We verhuizen kantoren, huisarts- en tandartspraktijken, winkels en publieke locaties, en verzorgen onder meer voor de Gemeente Den Haag de logistiek, opbouw en afbouw van alle stemlocaties bij verkiezingen, en de logistieke coördinatie bij World Forum The Hague.",
      },
    ],
  },

  {
    slug: "seniorenverhuizing",
    title: "Seniorenverhuizing",
    short: "Rustig verhuizen naar een appartement of zorginstelling, met extra aandacht.",
    intro:
      "Een verhuizing op latere leeftijd vraagt tijd, geduld en uitleg. Wij nemen die tijd. Vaak verhuizen we in overleg met kinderen of een zorgorganisatie en richten we de nieuwe woning dezelfde dag volledig in.",
    image: "senioren",
    seoTitle: "Seniorenverhuizing met extra zorg | Vakkundig",
    seoDescription:
      "Seniorenverhuizing naar appartement of zorginstelling: rustig tempo, nieuwe woning dezelfde dag ingericht. 4,9/5 uit 507 reviews. Bel 085 060 1023.",
    points: [
      "Rustig tempo en duidelijke uitleg bij elke stap",
      "Nieuwe woning dezelfde dag bewoonbaar opgeleverd",
      "Bed opgemaakt, tv aangesloten, gordijnen gehangen",
      "Hulp bij het uitzoeken van wat mee gaat en wat niet",
    ],
    body: [
      {
        heading: "Alles geregeld, ook het kleine werk",
        text: "We hangen lampen op, sluiten apparaten aan en zetten de kasten in zoals ze thuis stonden. Bij aankomst staat er koffie en is de woning geen bouwplaats meer, maar een thuis.",
      },
      {
        heading: "Samen met familie of zorginstelling",
        text: "Wij overleggen graag met kinderen, mantelzorgers of de zorginstelling. Zo weet iedereen wat er wanneer gebeurt en hoeven er geen dubbele afspraken gemaakt te worden.",
      },
    ],
  },
  {
    slug: "ontruiming",
    title: "Ontruiming",
    short:
      "Woningen en bedrijfspanden leeg, bezemschoon en verhuurbaar opgeleverd, inclusief grondige reiniging.",
    intro:
      "Wij ontruimen dagelijks woningen en bedrijfspanden en leveren ze compleet leeg en bezemschoon op. Voor particulieren én zakelijke opdrachtgevers: van een appartement na een huurperiode tot een kantoor, winkel of bedrijfsruimte. Waar gevraagd reinigen we de keuken, badkamer, toilet en ramen grondig, zodat het pand direct weer verhuurbaar of overdraagbaar is.",
    image: "ontruiming",
    seoTitle: "Woningontruiming & bedrijfsontruiming | Vakkundig Verhuizingen",
    seoDescription:
      "Woningontruiming in Den Haag en heel Zuid-Holland: woning of bedrijfspand laten ontruimen, leeg, bezemschoon en gereinigd opgeleverd, met gescheiden afvoer. Snel in te plannen.",
    points: [
      "Volledig leeg en bezemschoon opgeleverd, klaar voor de eindinspectie",
      "Grondige reiniging van keuken, badkamer, toilet, ramen en vloeren",
      "Particulier en zakelijk: woningen, appartementen, kantoren en bedrijfsruimtes",
      "Gescheiden afvoer, waar mogelijk hergebruik of naar een goed doel",
      "Snel in te plannen, ook op korte termijn en buiten kantooruren",
    ],
    body: [
      {
        heading: "Leeg, bezemschoon en weer verhuurbaar",
        text: "We halen de complete inboedel eruit, inclusief vloerbedekking, gordijnen, lampen en losse kasten, en voeren alles gescheiden af. Daarna leveren we het pand bezemschoon op. Kies je voor de complete oplevering, dan reinigen we ook de keuken, badkamer en het toilet grondig, zemen we de ramen en nemen we kozijnen en binnendeuren mee. Zo kan de verhuurder, makelaar of nieuwe gebruiker er meteen in.",
      },
      {
        heading: "Voor particulieren: rustig, discreet en zorgvuldig",
        text: "Bij een ontruiming na een overlijden, een verhuizing naar een zorginstelling of het einde van een huurcontract nemen we de tijd. We spreken vooraf af wat bewaard moet blijven, wat weg mag en wat gedoneerd wordt. Foto's, papieren en sieraden leggen we altijd apart, zodat niets per ongeluk verdwijnt.",
      },
      {
        heading: "Voor zakelijke opdrachtgevers: planmatig en op tijd",
        text: "Woningcorporaties, verhuurders, makelaars, beheerders en bedrijven werken met harde opleverdata. Wij plannen de ontruiming daar strak op, werken zo nodig 's avonds of in het weekend en leveren op met foto's van het eindresultaat. Meerdere units of een compleet pand in één keer? Dan schalen we op met extra mensen, wagens en containers.",
      },
      {
        heading: "Inclusief het laatste kleine werk",
        text: "Moeten er nog gaatjes gedicht, plafondhaken of beugels verwijderd of schroeven uit de wand worden gehaald voor de eindinspectie? Dat pakken we in dezelfde opdracht mee, zodat je er niet nog een partij bij hoeft te halen.",
      },
    ],
    faq: [
      {
        q: "Wat kost een woningontruiming?",
        a: "De prijs hangt af van de hoeveelheid inboedel, de verdieping en bereikbaarheid, de afvoerkosten en of je ook de eindschoonmaak wilt. Na een korte opname — live of met foto's — krijg je één vaste prijs voor de complete ontruiming.",
      },
      {
        q: "Hoe snel kunnen jullie een woning ontruimen in Den Haag?",
        a: "Meestal binnen enkele dagen, en bij spoed vaak de volgende dag. We werken zeven dagen per week, ook rond harde opleverdata van een verhuurder of makelaar.",
      },
      {
        q: "Wat gebeurt er met de inboedel?",
        a: "Wat nog goed is, geven we waar mogelijk een tweede leven via hergebruik of een goed doel. De rest voeren we gescheiden en verantwoord af via een erkende verwerker.",
      },
    ],
  },

  {
    slug: "opslag",
    title: "Opslag",
    short: "Tijdelijk je inboedel veilig stallen, van een paar dagen tot een paar maanden.",
    intro:
      "Sleutels van je nieuwe woning nog niet, verbouwing nog niet klaar of even ruimte nodig? Wij slaan je inboedel droog en veilig op en brengen hem terug wanneer jij klaar bent.",
    image: "opslag",
    seoTitle: "Verhuizen en opslag: inboedelopslag & tijdelijke opslag | Vakkundig",
    seoDescription:
      "Verhuizen en opslag in één hand: inboedelopslag en tijdelijke opslag van meubels in droge, beveiligde ruimte. Wij halen en brengen. Vraag een offerte aan.",
    points: [
      "Droge, schone en beveiligde opslagruimte",
      "Per week of per maand, zonder lange contracten",
      "Wij halen en brengen, je hoeft zelf niets te sjouwen",
      "Inboedel verpakt en op pallets, met inventarislijst",
    ],
    body: [
      {
        heading: "Verhuizen en opslag in één keer geregeld",
        text: "Loopt je verhuizing niet gelijk met de sleuteloverdracht? Dan verhuizen we in twee stappen: je inboedel gaat eerst in tijdelijke opslag en zodra je nieuwe woning klaar is, brengen we alles naar binnen. Eén partij, één planning, één prijs — je hoeft geen aparte opslagruimte te zoeken.",
      },
      {
        heading: "Van deur tot opslag en terug",
        text: "We halen je spullen op, verpakken ze voor opslag en zetten alles op een vaste plek. Bij levering brengen we alles weer naar binnen en zetten we het terug waar jij het wil hebben.",
      },
      {
        heading: "Inboedelopslag voor meubels, witgoed en dozen",
        text: "Meubels, witgoed, dozen en kwetsbare spullen gaan verpakt in wikkelfolie en verhuisdekens de opslag in, op pallets en van de vloer. Ook voor opslag van kantoorinventaris of een deel van je inboedel tijdens een verbouwing.",
      },
      {
        heading: "Overzicht van wat er staat",
        text: "Je krijgt een lijst van wat er in opslag staat. Moet er iets tussentijds uit? Dat kan in overleg gewoon geregeld worden.",
      },
    ],

  },
  {
    slug: "transport",
    title: "Transport",
    short:
      "Van vleugelpiano en ligbad tot complete keukens, IT-materiaal en evenementendecor: wij vervoeren het vakkundig.",
    intro:
      "Het maakt ons niet uit wát er vervoerd moet worden. Een piano of vleugelpiano, een ligbad, een complete keuken, beveiligd transport van IT-apparatuur en laadpunten, honderden stoelen en tafels voor een evenement of een decorbol van drie meter doorsnee: wij hebben altijd een vakkundige oplossing. Wij denken met u mee, bedenken de route en het hijsplan en bezorgen ook het transport dat anderen onmogelijk noemen.",
    image: "team",
    seoTitle: "Transport op maat: piano, keuken, bad & evenementen | Vakkundig",
    seoDescription:
      "Speciaal transport door heel Nederland: piano- en vleugeltransport, ligbaden, keukens, beveiligd IT-materiaal, stoelen, tafels en decor. Bel 085 060 1023.",
    points: [
      "Piano- en vleugeltransport met hijsbanden, piano-steekwagen en verhuislift",
      "Groot, zwaar en kwetsbaar: ligbaden, keukens, kluizen, decor en kunstobjecten",
      "Beveiligd transport van IT-materiaal, laadpunten en gevoelige apparatuur",
      "Stoelen, tafels, balies en decor voor evenementen, per stuk of per honderden",
      "Losse ritten en spoedvervoer door heel Nederland en België",
      "Verzekerd vervoer: op dekens, in folie, op pallets en vastgezet met spanbanden",
    ],
    body: [
      {
        heading: "Piano's en vleugels: ons specialisme",
        text: "Een piano of vleugel is zwaar, topzwaar en onvervangbaar. Wij pakken het instrument volledig in met dekens en wikkelfolie, zetten het op onze speciale piano-steekwagen en tillen het waar nodig met hijsbanden of via de verhuislift naar buiten. Trappen, smalle gangen, een tuin met grind of een balkon op de vierde verdieping: wij bekijken vooraf welke route en welk materieel nodig is, zodat uw instrument zonder één beschadiging op zijn nieuwe plek staat.",
      },
      {
        heading: "Groot, zwaar of kwetsbaar? Wij bedenken de oplossing",
        text: "Ligbaden, complete keukens, kluizen, machines, kunstobjecten en decorstukken die nergens door passen: dit is precies het werk waar wij goed in zijn. Wij meten op, demonteren waar nodig, beschermen elk oppervlak en kiezen het juiste hulpmiddel — van rolwagens en hijsbanden tot laadklep en verhuislift. Waar anderen afhaken, komen wij met een plan.",
      },
      {
        heading: "Beveiligd en zakelijk transport",
        text: "Voor bedrijven, gemeenten en vervoerders verzorgen wij beveiligd transport van IT-apparatuur, laadpunten en gevoelige apparatuur. Alles in kratten, op pallets of in eigen verpakking, met vaste contactpersoon, duidelijke tijdvensters en een team dat representatief is en gewend is aan toegangscontrole op locatie.",
      },
      {
        heading: "Evenementen: stoelen, tafels en decor op tijd op locatie",
        text: "Voor congressen, beurzen, festivals en internationale evenementen zoals de NATO-top in Den Haag rijden wij meubilair, balies, vergadertafels, stoelen en decor aan en af. Strak gepland, op de minuut, en na afloop weer netjes retour of gescheiden afgevoerd.",
      },
      {
        heading: "Eén rit of vaste afspraken",
        text: "Voor particulieren rijden we losse ritten, vaak nog dezelfde dag. Voor bedrijven maken we graag vaste afspraken en tarieven, bijvoorbeeld voor wekelijkse leveringen, retourvracht of terugkerende evenementenlogistiek. Waarom moeilijk doen als het vakkundig kan?",
      },
    ],
    faq: [
      {
        q: "Vervoeren jullie ook piano's en vleugels?",
        a: "Ja, pianotransport is een van onze specialismen. Wij verpakken het instrument volledig, gebruiken piano-steekwagens en hijsbanden en zetten waar nodig de verhuislift in. Zo gaat uw piano of vleugel veilig mee, ook uit een bovenwoning.",
      },
      {
        q: "Wat als mijn spullen extreem groot of zwaar zijn?",
        a: "Vertel ons wat het is en waar het staat, het liefst met een foto en de maten. Wij bekijken de route, kiezen het juiste materieel en komen met een oplossing — ook voor ligbaden, keukens, kluizen en groot decor.",
      },
      {
        q: "Kunnen jullie beveiligd of gevoelig materiaal vervoeren?",
        a: "Ja. Wij rijden regelmatig IT-apparatuur, laadpunten en gevoelige apparatuur voor zakelijke opdrachtgevers, met vaste contactpersonen, afgesproken tijdvensters en ervaring met beveiligde locaties.",
      },
    ],
  },
  {
    slug: "evenementen",
    title: "Evenementenlogistiek",
    short:
      "Op- en afbouwlogistiek voor congressen, festivals, beurzen en grote internationale evenementen.",
    intro:
      "Een evenement staat of valt bij de logistiek. Wij brengen podiumdelen, standbouw, meubilair, koeling en geluidsapparatuur op tijd op locatie, helpen bij de opbouw en halen na de laatste bezoeker alles weer op. Met jarenlange ervaring op locaties als het World Forum in Den Haag en bij projecten voor de gemeente Den Haag weten wij hoe strak zulke planningen zijn en hoe je daarbinnen rustig en foutloos blijft werken.",
    image: "evenementen",
    points: [
      "Ervaring met congressen, festivals, beurzen, verkiezingen en internationale topontmoetingen",
      "Aanvoer en afvoer op afgesproken tijdslot, ook 's nachts en in het weekend",
      "Laden en lossen op locatie, met steekwagens, rolcontainers en laadklep",
      "Werken binnen venstertijden, veiligheidszones en op afgezette evenemententerreinen",
      "Tussentijdse opslag van materialen tussen twee events",
    ],
    body: [
      {
        heading: "Vaste partner bij grote evenementen in Den Haag",
        text: "Wij werken samen met de gemeente Den Haag en zijn een bekend gezicht in en rond het World Forum. Van de opbouw rond de NATO-top en internationale conferenties tot de logistiek van verkiezingen: wij verzorgen het transport, de inrichting en de afbouw. Op dat soort locaties gelden strikte tijdvensters, toegangscontrole en veiligheidseisen — onze mensen zijn daarop ingespeeld, zijn representatief gekleed en weten dat afspraken op de minuut gelden.",
      },
      {
        heading: "Jarenlange ervaring, van festival tot staatsbezoek",
        text: "In de loop der jaren hebben wij vrijwel elk type evenement gedaan: buitenfestivals en markten met modderige terreinen en krappe aanvoerroutes, beurzen en congressen met honderden tafels, stoelen en standdelen, en zakelijke of overheidsevenementen waar de zaal binnen enkele uren volledig moet worden omgebouwd. Die ervaring betekent dat wij vooraf zien waar het knelt en dat wij ter plekke doorpakken als de planning schuift.",
      },
      {
        heading: "Opbouw en afbouw op schema",
        text: "Wij stemmen vooraf af wanneer welk deel op locatie moet staan. Zo staat de standbouw er voordat de decoratie komt en blijft de opbouwploeg niet wachten. We werken met een vast aanspreekpunt dat kort schakelt met de organisatie, de locatie en de technische ploegen. Na het evenement breken we in één keer af, voeren we materiaal gescheiden af en rijden we alles terug naar het depot of direct naar de volgende locatie.",
      },
      {
        heading: "Van marktkraam tot volledige beursstand",
        text: "We rijden voor organisatoren, standbouwers, cateraars, gemeenten en verenigingen. Klein materiaal gaat in kratten en op pallets, kwetsbare techniek in hoezen en op dekens, alles vastgezet met spanbanden. Ook het inrichten zelf pakken wij op: tafels, stoelen, balies, banners en meubilair op de juiste plek volgens plattegrond, zodat de zaal bij oplevering klaar is voor de eerste gast.",
      },
      {
        heading: "Evenementenlogistiek in Den Haag, Rotterdam en heel Zuid-Holland",
        text: "Onze thuisbasis is Den Haag: het World Forum, het Congresgebouw, de Grote Markt, Scheveningen, het Malieveld en de vele hotels en kantoorlocaties in de stad kennen wij van binnenuit. Daarnaast rijden wij dagelijks naar evenementenlocaties in Rotterdam, Delft, Leiden, Zoetermeer, Rijswijk, Westland, Gouda en Dordrecht, en op aanvraag door heel Nederland — van Utrecht en Amsterdam tot Eindhoven en Groningen. Zoek je een evenementenlogistiek partner of verhuisbedrijf voor beursopbouw in Zuid-Holland, dan staan wij met eigen wagens, verhuisliften en materiaal binnen korte tijd op locatie.",
      },
      {
        heading: "Voor organisatoren, standbouwers, gemeenten en cateraars",
        text: "Wij werken voor evenementenbureaus, standbouwers, AV- en lichttechniek, cateraars, gemeenten, ambassades en congresorganisaties. Denk aan het transport van podiumdelen en truss, het plaatsen van honderden stoelen en tafels volgens plattegrond, het aanvoeren van beursstands en balies, het verplaatsen van meubilair tussen zalen tijdens een omgebouwd programma en het gescheiden afvoeren van materiaal na afbouw. Alles onder één aanspreekpunt, met korte lijnen en heldere afspraken over tijd, toegang en oplevering.",
      },
      {
        heading: "Waarom klanten voor Vakkundig kiezen",
        text: "Wij zijn een jong en dynamisch team dat van aanpakken houdt: snel, efficiënt en met oog voor detail. Onze mensen zijn representatief, spreken Nederlands en Engels en zijn gewend te werken op locaties met toegangscontrole en veiligheidszones. Eigen bakwagens met laadklep, aanhangers, rolcontainers, steekwagens, verhuisliften en beschermmateriaal betekenen dat wij niets hoeven te huren en dus flexibel blijven als de planning verandert. Voor tussentijdse opslag van standmateriaal en meubilair beschikken wij over droge, beveiligde ruimte in de regio Den Haag.",
      },
    ],
    seoTitle: "Evenementenlogistiek Den Haag & Zuid-Holland | Op- en afbouw",
    seoDescription:
      "Evenementenlogistiek in Den Haag, Rotterdam en heel Zuid-Holland: transport, op- en afbouw en inrichting voor congressen, beurzen en festivals. Bel 085 060 1023.",
    faq: [
      {
        q: "Verzorgen jullie evenementenlogistiek buiten Den Haag?",
        a: "Ja. Den Haag en het World Forum zijn onze thuisbasis, maar wij rijden dagelijks naar Rotterdam, Delft, Leiden, Zoetermeer, Rijswijk, Westland, Gouda en Dordrecht en op aanvraag door heel Nederland.",
      },
      {
        q: "Kunnen jullie 's nachts of in het weekend opbouwen en afbouwen?",
        a: "Dat is bij evenementen eerder regel dan uitzondering. Wij werken binnen het afgesproken tijdslot, ook 's nachts, vroeg in de ochtend en in het weekend, zodat de zaal op tijd klaar is voor de eerste gast.",
      },
      {
        q: "Kunnen jullie materiaal tussen twee evenementen opslaan?",
        a: "Ja. Wij hebben droge, beveiligde opslag in de regio Den Haag voor standmateriaal, meubilair en techniek. Wij halen en brengen het materiaal op de afgesproken data.",
      },
      {
        q: "Wat kost evenementenlogistiek?",
        a: "Dat hangt af van het volume, de locatie, het tijdslot en of wij alleen transporteren of ook op- en afbouwen en inrichten. Stuur ons de plattegrond en planning, dan ontvang je een duidelijke offerte op maat.",
      },
      {
        q: "Leveren jullie ook personeel voor op- en afbouw op locatie?",
        a: "Ja. Naast transport zetten wij ervaren op- en afbouwploegen in die tafels, stoelen, balies, banners en meubilair volgens plattegrond op de juiste plek zetten en na het evenement in één keer afbreken.",
      },
    ],
  },

  {
    slug: "inpakservice",
    title: "Inpakservice",
    short:
      "Inpakservice met demontage en montage van kasten en bedden, inclusief verhuislift waar dat tijd bespaart.",
    intro:
      "Onze inpakservice is meer dan dozen vullen: wij pakken uw inboedel professioneel in, demonteren kasten, bedden en meubels die niet door een deur, trap of gang passen, en monteren alles op het nieuwe adres weer netjes op. Waar het kan zetten we onze verhuislift in, want dan hoeft er vaak juist níet gedemonteerd te worden en gaat de kast of bank in één keer heel naar binnen. Dat scheelt tijd, risico en kosten.",
    image: "inpakken",
    seoTitle: "Inpakservice met demontage en montage | Vakkundig",
    seoDescription:
      "Inpakservice door professionals: dozen, folie en dekens, demontage en montage van kasten en bedden, verhuislift waar dat sneller is. Gratis opname en offerte.",
    points: [
      "Professionele verhuisdozen, dekens, hoezen en wikkelfolie",
      "Demontage en montage van kasten, bedden en grote meubels",
      "Verhuislift waar dat sneller is dan demonteren en sjouwen",
      "Extra aandacht voor glaswerk, kunst, witgoed en kwetsbare bezittingen",
      "Volledig huis, alleen de keuken of alleen de kwetsbare spullen: alles is mogelijk",
    ],
    body: [
      {
        heading: "Eerst een bezichtiging: hoeveel m³, hoeveel dozen en hoeveel materiaal",
        text: "Elke inpakservice begint met een opname bij u thuis. Wij schatten hoeveel m³ inboedel u heeft en welke wagen daarvoor nodig is, hoeveel verhuisdozen en garderobedozen wij meenemen en hoeveel folie en dekens er nodig zijn. Ook bepalen we of demontage nodig is of dat de verhuislift een slimmere en snellere oplossing is. Zo weet u vooraf precies wat er gebeurt en wat het kost, zonder verrassingen achteraf.",
      },
      {
        heading: "Een compleet huis inpakken doen we meestal in twee dagen",
        text: "Bij een complete inpakservice werken we in twee dagen. Dag één staat volledig in het teken van inpakken: kamer voor kamer alles in dozen, grote meubels beschermd met wikkelfolie en verhuisdekens, kasten en bedden gedemonteerd en alles klaargelegd voor de volgende dag. Dag twee laden we in, rijden we naar het nieuwe adres, lossen we uit en monteren we uw bedden, kasten en meubels weer op de juiste plek.",
      },
      {
        heading: "Nog rustig een nachtje slapen in uw eigen bed",
        text: "Wij adviseren klanten om vooraf een koffer te maken met kleding en toiletspullen voor de laatste nacht. De bedden van de kinderen en die van u laten we op dag één zoveel mogelijk staan of monteren we op dag twee als eerste weer op, zodat u die laatste nacht gewoon nog thuis kunt slapen en de eerste avond in de nieuwe woning meteen een opgemaakt bed heeft.",
      },
      {
        heading: "Alleen de keuken of een deel van het huis? Dat doen we op één dag",
        text: "Gaat het alleen om de keuken, de studeerkamer of de kwetsbare spullen, dan combineren we het inpakken en verhuizen doorgaans op dezelfde dag. Van servies en glaswerk tot apparatuur en witgoed: alles gaat beschermd de wagen in en komt op het nieuwe adres op de juiste plek terug.",
      },
      {
        heading: "Planning en uitvoering nemen we samen door",
        text: "Tijdens de opname bespreken we de volledige planning: welke dagen, welke werkzaamheden, hoeveel verhuizers en welk materiaal. Onze klantenservice op kantoor denkt met u mee en helpt u stap voor stap verder, van voorbereiding tot oplevering. Bel ons gerust of stuur een e-mail.",
      },
    ],
  },

  {
    slug: "verhuislift",
    title: "Verhuisliftservice",
    short: "Verhuislift huren met bediening door onze eigen verhuizers, ook zonder complete verhuizing.",
    intro:
      "Verhuislift huren vanaf € 95 per uur, inclusief bediening door onze eigen verhuizers. Past de bank niet door het trappenhuis of woon je op vier hoog zonder lift? Dan zetten wij onze verhuislift in. Je kunt de lift met bediening ook los boeken, dus zonder dat wij je hele verhuizing doen.",
    image: "verhuislift",
    seoTitle: "Verhuislift huren vanaf € 95 p/u met bediening (26 m) | Vakkundig",
    seoDescription:
      "Verhuislift huren vanaf € 95 per uur, tot acht hoog (26 meter), inclusief bediening door onze eigen verhuizers. Ook los te boeken zonder complete verhuizing. Bel 085 060 1023.",
    points: [
      "Vanaf € 95 per uur, inclusief bediener",
      "Verhuislift acht hoog 26 meter",
      "Vaak geen demontage nodig: de bank of kast gaat in één keer heel naar binnen",
      "Sneller klaar en minder sjouwen op de trap, dus lagere kosten en minder risico",
      "Los te boeken per uur of per halve dag, ook als je zelf verhuist",
      "Voor banken, kasten, matrassen, wasmachines, witgoed en verhuisdozen",
    ],
    body: [
      {
        heading: "Geen overlast voor het trappenhuis en de buren",
        text: "Met een verhuislift blijft het trappenhuis vrij: geen geblokkeerde gang, geen liftgebruik van het gebouw en dus geen buren die op hun eigen lift staan te wachten. Alles gaat via het raam of balkon naar binnen, rustig en met korte lijnen tussen wagen en woning.",
      },
      {
        heading: "Wat kost een verhuislift huren?",
        text: "Onze verhuislift huur je vanaf € 95 per uur, inclusief een ervaren bediener van ons eigen team. Je betaalt dus voor de tijd dat de lift werkt, zonder verborgen kosten voor bediening. Boek je de lift bij een complete verhuizing, dan zit hij in één totaalprijs.",
      },
      {
        heading: "Vaak goedkoper dan trapwerk vanaf drie hoog",
        text: "Vanaf ongeveer drie hoog worden er bij verhuizingen meestal trap- of etagetoeslagen gerekend. Die kosten liggen vaak op hetzelfde niveau als een verhuislift, terwijl de lift sneller en makkelijker werkt. Laat je adviseren door ons kantoor: zij doen dit al jaren en denken graag met je mee over de slimste en voordeligste aanpak.",
      },

      {
        heading: "Vooraf checken wat nodig is",
        text: "We bekijken foto's of komen langs om te zien of de lift past: de breedte van de stoep, bomen, bovenleidingen en de hoogte van de verdieping. Daarna weet je precies hoe lang we nodig hebben en wat het kost.",
      },
    ],
    faq: [
      {
        q: "Wat kost een verhuislift huren?",
        a: "Een verhuislift huren kost bij ons vanaf € 95 per uur, inclusief bediener. Bij een complete verhuizing nemen we de lift mee in één totaalprijs.",
      },
      {
        q: "Kan ik de verhuislift los huren als ik zelf verhuis?",
        a: "Ja. Je kunt de verhuislift met bediening los boeken, ook als je de rest van de verhuizing zelf doet. Bel 085 060 1023 of stuur een WhatsApp-bericht.",
      },
    ],
  },
];

export const getService = (slug: string) => services.find((s) => s.slug === slug);
